import type { OrderStatus } from "@/constants/enums";
import type { Order } from "@/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ORDER_STATUS_ENUM } from "@/constants/enums";
import { CONTENT_GUTTER, PAGE_PADDING, SHAPE } from "@/constants/layout";
import { DRAWER_VIEW } from "@/constants/views";
import { useAppDispatch, useAppSelector } from "@/store";
import { openDrawer } from "@/store/slices/commonSlice";
import { loadRecentOrders } from "@/store/slices/historySlice";
import { formatCurrency } from "@/utils/format";

/**
 * The customer's recent orders, as a horizontally-scrolling rail inline on the
 * menu page.
 *
 * Rebuilt from the original's `rZ` in `recovery-source/live-chunks/mod-9802.js`
 * — full spec in `recovery-source/SPEC_recent-orders.md`. This is NOT a drawer:
 * an earlier pass had modelled order history as a `RECENT_ORDERS_DRAWER` list,
 * a view the original does not have. Clicking a card here opens the original's
 * real drawer, `RECENT_ORDER_DETAILS_DRAWER`, for that one order.
 */

/* -------------------------------- styling --------------------------------- */

/**
 * `rS`. The rail is a full-width band inside the menu's scroll column, so it
 * carries the same wide-screen gutters every other section does.
 */
const Section = styled(Stack)(({ theme }) => ({
  borderRadius: SHAPE.borderRadius,
  marginTop: 0,
  marginRight: PAGE_PADDING,
  marginBottom: theme.spacing(4),
  marginLeft: PAGE_PADDING,
  [theme.breakpoints.up("lg")]: {
    marginRight: CONTENT_GUTTER.lg,
    marginLeft: CONTENT_GUTTER.lg,
  },
  [theme.breakpoints.up("xl")]: {
    marginRight: CONTENT_GUTTER.xl,
    marginLeft: CONTENT_GUTTER.xl,
  },
  [theme.breakpoints.up("xxl")]: {
    marginRight: CONTENT_GUTTER.xxl,
    marginLeft: CONTENT_GUTTER.xxl,
  },
  [theme.breakpoints.up("xxxl")]: {
    marginRight: CONTENT_GUTTER.xxxl,
    marginLeft: CONTENT_GUTTER.xxxl,
  },
}));

/** `r_` */
const HeadingRow = styled(Stack)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: PAGE_PADDING,
});

/** `rP` — uppercased by CSS, not by the string. */
const Heading = styled(Typography)({
  marginBottom: "12px",
  fontWeight: "bold",
  fontSize: "20px",
  textTransform: "uppercase",
  letterSpacing: "1px",
});

/** `rE` — the scroll port. Scrollbar hidden in both engines. */
const Scroller = styled(Stack)({
  display: "flex",
  overflow: "hidden",
  overflowX: "auto",
  scrollbarWidth: "none",
  paddingBottom: 8,
  marginTop: 5,
  "&::-webkit-scrollbar": { display: "none" },
});

/** `rk` — the 36px circular scroll button. */
const ScrollButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`,
  borderRadius: "50%",
  transition: theme.transitions.create(["all"], { duration: 300 }),
  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[2],
    transform: "scale(1.05)",
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
    color: theme.palette.text.disabled,
    borderColor: theme.palette.action.disabledBackground,
  },
}));

/** `rb` */
const OrderCard = styled(Card)(({ theme }) => ({
  minWidth: 240,
  maxWidth: 280,
  height: 160,
  borderRadius: 12,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
  "& .MuiCardActionArea-root": { height: "100%", borderRadius: 12 },
}));

/* -------------------------------- helpers --------------------------------- */

/** `rm` — status to emoji. */
const statusEmoji = (status?: OrderStatus | string): string => {
  switch (status) {
    case ORDER_STATUS_ENUM.COMPLETED:
    case ORDER_STATUS_ENUM.DELIVERED:
      return "✓";
    case ORDER_STATUS_ENUM.PREPARING:
      return "⏳";
    case ORDER_STATUS_ENUM.PLACED:
    case ORDER_STATUS_ENUM.UNCONFIRMED:
      return "⏱️";
    case ORDER_STATUS_ENUM.CANCELLED:
      return "✗";
    default:
      return "📋";
  }
};

/**
 * `DEFAULT_ORDER_STATUS_COLOR_CODE_MAP` — the platform's own status palette,
 * shared with the POS. These are literal hexes in the original, not theme
 * colours, so they are reproduced literally.
 */
const STATUS_COLOR: Record<string, string> = {
  UNCONFIRMED: "#727472",
  PLACED: "#0000FF",
  PREPARING: "#307D7E",
  READY: "#FBB117",
  OUT_FOR_DELIVERY: "#FF5F1F",
  DELIVERED: "#800080",
  COMPLETED: "#4CC417",
  CANCELLED: "#FF0000",
};

/** `rf` */
const statusColor = (status?: OrderStatus | string): string =>
  STATUS_COLOR[String(status)] || "#9e9e9e";

/** The original's `moment(createdAt).format("MMM DD, YYYY")`. */
const formatOrderDate = (value?: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

/**
 * The original prints `PLATFORM_NAMES[order.platform]` — "Web Delivery",
 * "Web Take-away". `/web/*` projects the platform down to a bare order type, so
 * the same two words are rebuilt from it rather than invented.
 */
const ORDER_TYPE_LABEL: Record<string, string> = {
  DELIVERY: "Delivery",
  "TAKE-AWAY": "Take-away",
  "DINE-IN": "Dine-in",
};

/* ---------------------------------- card ---------------------------------- */

interface OrderCardProps {
  order: Order;
  currencySymbol?: string;
  currencyCode?: string;
  onClick: () => void;
}

function RecentOrderCard({
  order,
  currencySymbol,
  currencyCode,
  onClick,
}: OrderCardProps) {
  const color = statusColor(order.status);
  return (
    <OrderCard variant="outlined">
      <CardActionArea sx={{ height: "100%", p: 2 }} onClick={onClick}>
        {/* The corner wash is tinted with the status colour at 12.5% alpha
            (`${hex}20`), so a cancelled card reads red before you read it. */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 80,
            height: 80,
            background: `linear-gradient(135deg, ${color}20 0%, transparent 70%)`,
            borderRadius: "0 12px 0 50px",
            zIndex: 0,
          }}
        />
        <Stack spacing={1.5} sx={{ height: "100%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack spacing={0.5}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ fontSize: "0.9rem" }}
              >
                Order #{order.reference || order._id.slice(-6).toUpperCase()}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {formatOrderDate(order.placed_at)}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", color: "text.secondary" }}
            >
              {ORDER_TYPE_LABEL[String(order.order_type)] ?? ""}
            </Typography>
          </Stack>

          {order.status && (
            <Chip
              size="small"
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>{statusEmoji(order.status)}</span>
                  <span>{String(order.status).toUpperCase()}</span>
                </Stack>
              }
              sx={{
                width: "fit-content",
                fontSize: "0.7rem",
                height: "24px",
                backgroundColor: color,
                color: "white",
                fontWeight: 600,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}

          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: "primary.main", mt: "auto" }}
          >
            {formatCurrency(order.total, {
              symbol: currencySymbol,
              code: currencyCode,
            })}
          </Typography>
        </Stack>
      </CardActionArea>
    </OrderCard>
  );
}

/* -------------------------------- skeleton -------------------------------- */

/** `ry`. Its geometry deliberately differs from the real card, as in the original. */
function RecentOrderSkeleton() {
  return (
    <Card
      sx={{
        minWidth: { xs: 200, sm: 220, md: 240 },
        maxWidth: { xs: 240, sm: 260, md: 280 },
        width: "100%",
        height: { xs: 140, sm: 150, md: 160 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%" }}>
        <Stack spacing={1.5} sx={{ height: "100%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack spacing={0.5}>
              <Skeleton variant="text" width={80} height={18} />
              <Skeleton variant="text" width={70} height={14} />
            </Stack>
            <Skeleton variant="text" width={35} height={14} />
          </Stack>
          <Skeleton
            variant="rectangular"
            width={70}
            height={20}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton variant="text" width={55} height={20} sx={{ mt: "auto" }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------- rail ---------------------------------- */

interface RecentOrdersRailProps {
  /**
   * The scroll target its synthetic category tab jumps to. The original keeps a
   * ref per section and scrolls by `offsetTop`; this tree scrolls the same
   * sections by id, so the id is the section's handle.
   */
  id?: string;
  /** Reports "there is at least one recent order" upward. Nothing else. */
  setHasRecentOrders?: (value: boolean) => void;
}

function RecentOrdersRail({ id, setHasRecentOrders }: RecentOrdersRailProps) {
  const dispatch = useAppDispatch();
  const {
    _id: tenantId,
    currency_symbol: currencySymbol,
    currency_code: currencyCode,
  } = useAppSelector((state) => state.settings.data);
  const isAuthCheckComplete = useAppSelector(
    (state) => state.common.isAuthCheckComplete,
  );
  const orderIds = useAppSelector((state) => state.auth.customer?.order_ids);
  const { recentOrders, recentOrdersLoading } = useAppSelector(
    (state) => state.history,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** Exactly `slice(0, 20)` — no filtering, no sorting, no dedupe. */
  const ids = useMemo(() => (orderIds ?? []).slice(0, 20), [orderIds]);

  useEffect(() => {
    if (tenantId && ids.length && isAuthCheckComplete) {
      dispatch(loadRecentOrders(ids));
    }
  }, [dispatch, tenantId, ids, isAuthCheckComplete]);

  useEffect(() => {
    setHasRecentOrders?.(recentOrders.length > 0);
  }, [setHasRecentOrders, recentOrders.length]);

  // Plain scroll + window-resize listeners, as in the original — no
  // ResizeObserver. The 1px slack on the right stops sub-pixel layout from
  // leaving the forward button permanently enabled at the end of the rail.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    const update = () => {
      setCanScrollLeft(element.scrollLeft > 0);
      setCanScrollRight(
        element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
      );
    };
    update();
    element.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      element.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [recentOrders.length]);

  const scroll = useCallback((direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -300 : 300,
      behavior: "smooth",
    });
  }, []);

  const openOrder = useCallback(
    (orderId: string) => {
      dispatch(
        openDrawer({
          view: DRAWER_VIEW.RECENT_ORDER_DETAILS,
          payload: { order_id: orderId },
        }),
      );
    },
    [dispatch],
  );

  /**
   * `_`. The buttons sit BESIDE the rail from 480px up and BELOW it under
   * 480px. 480 is a raw pixel value in the original, not one of the theme's
   * breakpoints, so it stays a raw media query here too.
   */
  const renderScrollButtons = (
    direction: "row" | "column",
    { hideBelow, hideAbove }: { hideBelow?: number; hideAbove?: number },
  ) => {
    const sx: Record<string, unknown> = {
      display: "flex",
      alignSelf: "center",
      ml: 1,
    };
    if (hideBelow !== undefined) {
      sx[`@media (max-width: ${hideBelow - 1}px)`] = { display: "none" };
    }
    if (hideAbove !== undefined) {
      sx[`@media (min-width: ${hideAbove}px)`] = { display: "none" };
    }
    return (
      <Stack direction={direction} spacing={1} sx={sx}>
        <ScrollButton
          size="small"
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          aria-label="scroll left"
          sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
        >
          <ArrowBackIcon fontSize="small" />
        </ScrollButton>
        <ScrollButton
          size="small"
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          aria-label="scroll right"
          sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
        >
          <ArrowForwardIcon fontSize="small" />
        </ScrollButton>
      </Stack>
    );
  };

  if (recentOrdersLoading) {
    return (
      <Section id={id}>
        <HeadingRow>
          <Heading variant="h5">Recent Orders</Heading>
        </HeadingRow>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Scroller ref={scrollRef} direction="row" spacing={2} sx={{ flex: 1 }}>
            <RecentOrderSkeleton />
            <RecentOrderSkeleton />
            <RecentOrderSkeleton />
          </Scroller>
          {/* While loading the pair is always vertical and always visible —
              the original does not apply its media queries to this branch. */}
          <Stack
            direction="column"
            spacing={1}
            sx={{ alignSelf: "center", ml: 1 }}
          >
            <ScrollButton
              size="small"
              disabled
              aria-label="scroll left"
              sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
            >
              <ArrowBackIcon fontSize="small" />
            </ScrollButton>
            <ScrollButton
              size="small"
              disabled
              aria-label="scroll right"
              sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
            >
              <ArrowForwardIcon fontSize="small" />
            </ScrollButton>
          </Stack>
        </Stack>
      </Section>
    );
  }

  // No empty state: the original renders nothing at all, and reports
  // `hasRecentOrders: false` upward so the section's tab disappears too.
  if (recentOrders.length === 0) return null;

  return (
    <>
      <Section id={id}>
        <HeadingRow>
          <Stack spacing={1}>
            <Heading variant="h5">Recent Orders</Heading>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Track your order history and reorder favorites
            </Typography>
          </Stack>
        </HeadingRow>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Scroller ref={scrollRef} direction="row" spacing={3} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={3} pt={2}>
              {recentOrders.map((order) => (
                <RecentOrderCard
                  key={order._id}
                  order={order}
                  currencySymbol={currencySymbol}
                  currencyCode={currencyCode}
                  onClick={() => openOrder(order._id)}
                />
              ))}
            </Stack>
          </Scroller>
          {renderScrollButtons("column", { hideBelow: 480 })}
        </Stack>
        {renderScrollButtons("row", { hideAbove: 480 })}
      </Section>
      <Divider sx={{ my: 2 }} />
    </>
  );
}

/**
 * `rw = memo(rZ, () => true)` — the rail never re-renders from its parent. Every
 * update arrives through its own redux subscriptions, which is what lets the
 * menu page re-render freely while a long list of cards stays put.
 */
export default memo(RecentOrdersRail, () => true);
